const HESTIA_PREVIEW_OVERRIDE_QUERY_PARAM = 'hestiaPreviewUrl'
const HESTIA_PREVIEW_OVERRIDE_STORAGE_KEY = 'owner-hestia-preview-url-override'

export const HESTIA_PREVIEW_OVERRIDE_TTL_MS = 24 * 60 * 60 * 1000

interface StoredHestiaPreviewOverride {
  expiresAt: number
  url: string
}

const allowedPreviewHosts = [
  'app.owner.com',
  'dev-app.owner.com',
  'eos-production.pages.dev',
  'eos-staging.pages.dev',
]

const resetValues = new Set(['clear', 'false', 'off', 'reset'])

const canUsePreviewOverride = (): boolean => {
  return typeof window !== 'undefined' && import.meta.env.MODE !== 'production'
}

const isLocalhost = (value: string): boolean => {
  return /^(127\.0\.0\.1|localhost)(:\d+)?(?:\/|$)/.test(value)
}

const hasProtocol = (value: string): boolean => {
  return /^[a-z]+:\/\//i.test(value)
}

const normalizePreviewUrl = (rawValue: string): string | undefined => {
  const trimmedValue = rawValue.trim()

  if (!trimmedValue) {
    return undefined
  }

  const candidate = hasProtocol(trimmedValue)
    ? trimmedValue
    : `${isLocalhost(trimmedValue) ? 'http' : 'https'}://${trimmedValue}`

  try {
    const parsedUrl = new URL(candidate)
    const isAllowedLocalhost =
      parsedUrl.protocol === 'http:' &&
      ['127.0.0.1', 'localhost'].includes(parsedUrl.hostname)

    if (!isAllowedLocalhost && parsedUrl.protocol !== 'https:') {
      return undefined
    }

    const isAllowedHost = allowedPreviewHosts.some((host) => {
      return (
        parsedUrl.hostname === host || parsedUrl.hostname.endsWith(`.${host}`)
      )
    })

    if (!isAllowedLocalhost && !isAllowedHost) {
      return undefined
    }

    return parsedUrl.origin
  } catch {
    return undefined
  }
}

const writeStoredPreviewOverride = (url: string): void => {
  const value: StoredHestiaPreviewOverride = {
    expiresAt: Date.now() + HESTIA_PREVIEW_OVERRIDE_TTL_MS,
    url,
  }

  try {
    localStorage.setItem(
      HESTIA_PREVIEW_OVERRIDE_STORAGE_KEY,
      JSON.stringify(value)
    )
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

const stripPreviewOverrideFromUrl = (): void => {
  const currentUrl = new URL(window.location.href)

  if (!currentUrl.searchParams.has(HESTIA_PREVIEW_OVERRIDE_QUERY_PARAM)) {
    return
  }

  currentUrl.searchParams.delete(HESTIA_PREVIEW_OVERRIDE_QUERY_PARAM)

  const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`

  window.history.replaceState(window.history.state, '', nextUrl || '/')
}

export const clearHestiaPreviewOverride = (): void => {
  if (!canUsePreviewOverride()) {
    return
  }

  try {
    localStorage.removeItem(HESTIA_PREVIEW_OVERRIDE_STORAGE_KEY)
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

export const getStoredHestiaPreviewOverride = (): string | undefined => {
  if (!canUsePreviewOverride()) {
    return undefined
  }

  try {
    const storedValue = localStorage.getItem(
      HESTIA_PREVIEW_OVERRIDE_STORAGE_KEY
    )

    if (!storedValue) {
      return undefined
    }

    const parsedValue = JSON.parse(
      storedValue
    ) as Partial<StoredHestiaPreviewOverride>

    if (
      typeof parsedValue.url !== 'string' ||
      typeof parsedValue.expiresAt !== 'number'
    ) {
      clearHestiaPreviewOverride()
      return undefined
    }

    if (Date.now() > parsedValue.expiresAt) {
      clearHestiaPreviewOverride()
      return undefined
    }

    return parsedValue.url
  } catch {
    clearHestiaPreviewOverride()
    return undefined
  }
}

export const consumeHestiaPreviewOverrideFromQuery = (): string | undefined => {
  if (!canUsePreviewOverride()) {
    return undefined
  }

  try {
    const currentUrl = new URL(window.location.href)
    const rawValue = currentUrl.searchParams.get(
      HESTIA_PREVIEW_OVERRIDE_QUERY_PARAM
    )

    if (rawValue === null) {
      return undefined
    }

    stripPreviewOverrideFromUrl()

    if (resetValues.has(rawValue.trim().toLowerCase())) {
      clearHestiaPreviewOverride()
      return undefined
    }

    const normalizedUrl = normalizePreviewUrl(rawValue)

    if (!normalizedUrl) {
      return undefined
    }

    writeStoredPreviewOverride(normalizedUrl)

    return normalizedUrl
  } catch {
    return undefined
  }
}
